import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import projects, { getProjectsForBrand } from "../config/projects";
import { EVENT_NAMES, PARAM_KEYS, trackEvent } from "../analytics";
import { useBrand } from "../branding/BrandContext";
import { BrandMark } from "../branding/BrandText";

export default function ServiceProviders() {
  const brand = useBrand();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectOptions = useMemo(
    () => getProjectsForBrand(brand?.brandKey),
    [brand?.brandKey]
  );

  const initialZip = searchParams.get("zip") || "";
  const initialCategory = searchParams.get("category") || "";
  const initialRadius = searchParams.get("radius") || "25";

  const [zip, setZip] = useState(initialZip);
  const [category, setCategory] = useState(initialCategory);
  const [radius, setRadius] = useState(initialRadius);

  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchProviders = async () => {
    const trimmed = (zip || "").trim();
    if (!trimmed) return;

    // Support multiple ZIP codes separated by commas or whitespace
    const zipTokens = trimmed
      .split(/[\s,]+/)
      .map((z) => z.trim())
      .filter(Boolean);

    if (zipTokens.length === 0) return;

    setLoading(true);

    try {
      const baseUrl =
        "https://api.home-helper.com/api/providers/search";

      const results = await Promise.all(
        zipTokens.map(async (z) => {
          const params = new URLSearchParams();
          params.set("zip", z);
          params.set("radius", radius);
          if (category) {
            params.set("category", category);
          }

          const res = await fetch(`${baseUrl}?${params.toString()}`);
          const data = await res.json();
          return data.providers || [];
        })
      );

      // Merge and de-duplicate providers across ZIP codes by provider_guid
      const merged = [];
      const seen = new Set();

      results.forEach((list) => {
        list.forEach((p) => {
          if (!p || !p.provider_guid) return;
          if (seen.has(p.provider_guid)) return;
          seen.add(p.provider_guid);
          merged.push(p);
        });
      });

      setProviders(merged);
    } catch (err) {
      console.error("Failed to load providers", err);
      setProviders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialZip) {
      fetchProviders();
    }
    // eslint-disable-next-line
  }, []);

  const handleSearch = () => {
    const zipTokens = (zip || "")
      .split(/[\s,]+/)
      .map((value) => value.trim())
      .filter(Boolean);

    trackEvent(EVENT_NAMES.BROWSE_PROVIDERS_CLICK, {
      [PARAM_KEYS.ZIP_CODE]: zipTokens.length === 1 ? zipTokens[0] : undefined,
      [PARAM_KEYS.ZIP_CODES]: zipTokens.length > 1 ? zipTokens : undefined,
      [PARAM_KEYS.CATEGORY_KEY]: category || undefined,
      [PARAM_KEYS.SEARCH_RADIUS_MILES]: Number(radius),
      [PARAM_KEYS.PLACEMENT]: "provider_list_search",
    });

    setSearchParams({ zip, category, radius });
    fetchProviders();
  };

  const handleProviderClick = (provider) => {
    trackEvent(EVENT_NAMES.PROVIDER_CARD_CLICK, {
      [PARAM_KEYS.PROVIDER_ID]: provider.provider_guid,
      [PARAM_KEYS.CATEGORY_KEY]: provider.primary_category || provider.category || category || undefined,
      [PARAM_KEYS.SEARCH_RADIUS_MILES]: Number(radius),
      [PARAM_KEYS.PLACEMENT]: "provider_list_results",
    });

    navigate(`/providers/${provider.provider_guid}`);
  };

  return (
    <>
      <Header />

      <main className="providersPage" aria-labelledby="providers-heading">

        {/* SEARCH HEADER */}
        <section className="providersHeader" aria-describedby="providers-intro">
          <div className="providersHeader-inner">
            <h1 id="providers-heading" className="providersTitle">
              Find local contractors
            </h1>
            <p id="providers-intro" className="providersSubtitle">
              Enter one or more ZIP codes to see approved <BrandMark /> pros in your area.
            </p>

            <div className="providersSearchCard" role="search" aria-label="Find service providers">
              <div className="providersSearchGrid">

                {/* Zip Code(s) */}
                <div className="providersField">
                  <label htmlFor="providers-zip" className="providersLabel">
                    ZIP code(s)
                  </label>
                  <input
                    id="providers-zip"
                    type="text"
                    placeholder="e.g. 91354 or 91354, 91355"
                    value={zip}
                    onChange={(e) => setZip(e.target.value)}
                    className="providersInput"
                  />
                </div>

                {/* Radius */}
                <div className="providersField providersField--compact">
                  <label htmlFor="providers-radius" className="providersLabel">
                    Radius
                  </label>
                  <select
                    id="providers-radius"
                    value={radius}
                    onChange={(e) => setRadius(e.target.value)}
                    className="providersSelect"
                  >
                    <option value="10">10 miles</option>
                    <option value="25">25 miles</option>
                    <option value="50">50 miles</option>
                    <option value="100">100 miles</option>
                  </select>
                </div>

                {/* Category Dropdown from projects.js */}
                <div className="providersField">
                  <label htmlFor="providers-category" className="providersLabel">
                    Category (optional)
                  </label>
                  <select
                    id="providers-category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="providersSelect"
                  >
                    <option value="">All project types</option>
                    {projectOptions.map((project) => (
                      <option key={project.key} value={project.slug}>
                        {project.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Search Button */}
                <div className="providersActions">
                  <button
                    type="button"
                    onClick={handleSearch}
                    className="providersSearchButton"
                  >
                    Search providers
                  </button>
                </div>
              </div>
              <p className="providersHelpText providersHelpText--below">
                Separate multiple ZIP codes with commas or spaces.
              </p>
            </div>
          </div>
        </section>

        {/* RESULTS SECTION */}
        <section className="providersResults" aria-live="polite">

          {loading && (
            <div className="providersStatus">
              Loading providers…
            </div>
          )}

          {!loading && providers.length === 0 && zip && (
            <div className="providersStatus">
              No providers found in this area.
            </div>
          )}

          {!loading && providers.length > 0 && (
            <div className="providersTableWrap" role="region" aria-label="Search results">
              <table className="providersTable">
                <caption className="visuallyHidden">
                  Service providers matching your search
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Business</th>
                    <th scope="col">Category</th>
                    <th scope="col">Description</th>
                  </tr>
                </thead>

                <tbody>
                  {providers.map((p) => {
                    // Prefer the canonical primary/secondary category fields
                    const primaryCategory = p.primary_category || null;
                    const fallbackCategory =
                      (Array.isArray(p.secondary_categories) && p.secondary_categories[0]) ||
                      p.category || "";

                    const effectiveCategory = primaryCategory || fallbackCategory || "";

                    const matchedProject = projects.find(
                      (proj) => proj.slug === effectiveCategory
                    );

                    return (
                      <tr
                        key={p.vendor_id}
                        className="providersRow"
                        onClick={() => handleProviderClick(p)}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleProviderClick(p);
                          }
                        }}
                      >
                        {/* Business + Image */}
                        <td className="providersCell providersCell-business">
                          <img
                            src={
                              p.image_url ||
                              "https://via.placeholder.com/60x60?text=Logo"
                            }
                            alt="Provider logo"
                            className="providersLogo"
                          />
                          <div>
                            <div className="providersBusinessName">
                              {p.company_name || "Unnamed Provider"}
                            </div>
                            {p.phone && (
                              <div className="providersPhone">
                                {p.phone}
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Category */}
                        <td className="providersCell">
                          {matchedProject
                            ? matchedProject.label
                            : effectiveCategory}
                        </td>

                        {/* Description */}
                        <td className="providersCell providersCell-description">
                          {p.description ||
                            "Professional service provider ready to help with your project."}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}
